import api from 'app/api/axios-api';
import { Project } from '@app/shared/types/project.type';

const BASE = '/ate-plus/atac';

export interface ATACProject {
  projectId: string;
  projectTitle: string;
  description?: string;
  domainName?: string;
  status: 'draft' | 'in_progress' | 'assessed' | 'certified';
  provider?: string;
  modelName?: string;
  modelVersion?: string;
  deploymentType?: string;
  intendedUse?: string;
  riskClassification?: string;
  assessmentOwner?: string;
  created_at?: string;
}

export interface ATACDocument {
  evidenceId: string;
  documentType: string;
  filename?: string;
  sourceUrl?: string;
  uploadedAt?: string;
}

export interface ATACRun {
  runId: string;
  status: 'pending' | 'completed' | 'failed';
  startedAt?: string;
  completedAt?: string;
  summaryCards?: {
    trustScore: number | null;
    trustGrade: string | null;
    certificationStatus: string | null;
    providerTrust: number | null;
    evidenceConfidence: number | null;
  };
  charts?: {
    trustGauge?: { value: number | null };
    trustRadar?: { dimensions: { key: string; label: string; score: number }[] };
    evidenceBar?: { bars: { key: string; label: string; value: number }[] };
    providerBar?: { bars: { key: string; label: string; value: number }[] };
  };
  certification?: {
    certificateId?: string;
    status?: string;
    conditions?: string[];
    overrideFindings?: string[];
    pdfUrl?: string;
    generatedAt?: string;
    expiresAt?: string;
  };
}

export interface ATACResultsResponse {
  status: number;
  project: { projectId: string; projectTitle: string; status: string };
  modelRegistration: {
    provider?: string;
    modelName?: string;
    modelVersion?: string;
    deploymentType?: string;
    intendedUse?: string;
    riskClassification?: string;
    assessmentOwner?: string;
    assessmentDate?: string;
  };
  latestRunId: string | null;
  runs: ATACRun[];
}

export interface ATACProvider {
  provider: string;
  displayName: string;
  models: { name: string; versions: string[] }[];
}

export const ATAC_DOCUMENT_TYPES = [
  'Model Card',
  'Technical Paper',
  'Release Notes',
  'Transparency Report',
  'Terms of Use',
  'Privacy Policy',
  'DPA',
  'Security Whitepaper',
  'Trust Center Report',
  'Benchmark Report',
  'ESG Report',
  'Other'
] as const;

export type ATACDocumentType = (typeof ATAC_DOCUMENT_TYPES)[number];

export const ATAC_DEPLOYMENT_TYPES = ['API', 'Enterprise API', 'On-Premise', 'SaaS'] as const;
export const ATAC_RISK_CLASSIFICATIONS = ['Low', 'Medium', 'High', 'Critical'] as const;

export function getATACUserId(): string {
  return localStorage.getItem('userId') || '';
}

// Projects
export const listATACProjects = (userId: string) =>
  api.get<{ projects?: ATACProject[] }>(`${BASE}/projects?userId=${userId}`);

export const createATACProject = (body: object) =>
  api.post<{ project?: ATACProject }>(`${BASE}/project/create`, {
    ...(body as Record<string, unknown>),
    userId: (body as { userId?: string }).userId ?? getATACUserId()
  });

export const getATACProject = (pid: string) => api.get<{ project?: ATACProject }>(`${BASE}/project/${pid}`);

// Providers dropdown source
export const listATACProviders = () => api.get<{ providers?: ATACProvider[] }>(`${BASE}/providers`);

// Documents
export const uploadATACDocument = (pid: string, form: FormData) =>
  api.post(`${BASE}/${pid}/documents/upload`, form, {
    headers: { 'Content-Type': 'multipart/form-data' }
  });

export const listATACDocuments = (pid: string) => api.get<{ documents?: ATACDocument[] }>(`${BASE}/${pid}/documents`);

export const deleteATACDocument = (pid: string, eid: string) => api.delete(`${BASE}/${pid}/documents/${eid}`);

// Analysis
export const runATACAnalysis = (pid: string) =>
  api.post<{ runId: string; status: number }>(`${BASE}/${pid}/analyze`, {});

export const getATACRunStatus = (pid: string, runId: string) =>
  api.get<{ status: 'pending' | 'completed' | 'failed'; error?: string }>(`${BASE}/${pid}/runs/${runId}/status`);

export const getATACResults = (pid: string) => api.get<ATACResultsResponse>(`${BASE}/${pid}/results`);

// Certificate
export const generateCertificate = (pid: string, runId: string) =>
  api.post<{ certificateId?: string; status: number }>(`${BASE}/${pid}/runs/${runId}/certificate/generate`, {});

export const downloadCertificate = (pid: string, runId: string) =>
  api.get(`${BASE}/${pid}/runs/${runId}/certificate/download`, { responseType: 'blob' });

/** Maps ATAC project records to ProjectCard shape for the all-projects list. */
export function mapATACProjectToProjectCard(raw: ATACProject): Project {
  return {
    projectId: raw.projectId,
    title: raw.projectTitle,
    overview: raw.description ?? '',
    domain: raw.domainName as Project['domain'],
    userId: getATACUserId(),
    organizationId: localStorage.getItem('organizationId') ?? '',
    createdOn: raw.created_at,
    atacStatus: raw.status,
    provider: raw.provider,
    modelName: raw.modelName
  };
}

export function getLatestCompletedRun(runs: ATACRun[] = []): ATACRun | null {
  return [...runs].reverse().find(r => r.status === 'completed') ?? null;
}

export function hasPendingRun(runs: ATACRun[] = []): boolean {
  return runs.some(r => r.status === 'pending');
}
